/**
 * Calendar Context
 * 
 * Provides calendar-specific state and functionality.
 * Now uses centralized data from AppDataProvider instead of fetching directly.
 */

import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { useAppData } from '../../../providers/AppDataProvider';
import { useRepeatingInvoice } from '../../invoices/contexts/RepeatingInvoiceContext';
import { CalendarEvent } from '../../job-summary/Interfaces';
import {
  UnifiedCalendarItem,
  CalendarItemType,
  CalendarFilterState,
  DEFAULT_CALENDAR_FILTERS,
  eventToCalendarItem,
  invoiceToCalendarItem,
  invoiceCreationToCalendarItem,
  repeatingInvoiceToCalendarItems,
  billToCalendarItem,
  jobToCalendarItem,
  toLocalDateString,
} from '../../../types/calendar';
import { addFrequency } from '../../../utils/recurrence';
import { useCalendarCommitments } from './useCalendarCommitments';
import { calendarCustomerId, mergeCalendarCommitments } from './calendarCommitments';

// ============================================
// Layer status (Task 5)
// ============================================

/** One status row per source the calendar merges. */
export type CalendarLayerKey = 'events' | 'jobs' | 'invoices' | 'recurring' | 'bills' | 'support' | 'stripe';

/**
 * Per-layer health so the UI can tell "nothing scheduled" apart from "this layer
 * never loaded". A month with no items used to look identical whether the data
 * was genuinely clear or the accounting read had failed.
 */
export interface CalendarLayerStatus {
  key: CalendarLayerKey;
  /** Human label, so the consumer does not re-invent copy per layer. */
  label: string;
  /**
   * The user's filter toggle for this layer. When false, `count` is 0 because
   * the layer is HIDDEN — never render that as "nothing scheduled".
   */
  enabled: boolean;
  /** First load still in flight. Only meaningful when `statusKnown` is true. */
  loading: boolean;
  /** Backing source is linked/usable. Only meaningful when `statusKnown` is true. */
  connected: boolean;
  /** Read error, or null. Only meaningful when `statusKnown` is true. */
  error: string | null;
  /** Items this layer contributed to `calendarItems` for the CURRENT range + filters. */
  count: number;
  /**
   * Whether loading/connected/error can be trusted at all.
   *
   * false for the invoice and bill layers: AppDataProvider's native Firestore
   * listeners for `invoices`/`bills` never touch `xeroLoading`/`xeroError` (they
   * only console.error on failure), and `xeroConnected` is pinned to a literal
   * `true` now that accounting is native rather than Xero-linked. So those three
   * flags say "loaded and connected" from the first frame no matter what actually
   * happened. Render an honest "unknown" for those layers rather than a
   * confident empty state — do not invent a "not connected" banner that can
   * never fire. Fixing the provider is out of this file's scope.
   */
  statusKnown: boolean;
}

export type CalendarLayerStatusMap = Record<CalendarLayerKey, CalendarLayerStatus>;

/** Which layer each item type belongs to, for counting. */
const LAYER_OF_TYPE: Record<CalendarItemType, CalendarLayerKey> = {
  event: 'events',
  job_due: 'jobs',
  invoice_due: 'invoices',
  invoice_created: 'invoices',
  recurring_invoice: 'recurring',
  recurring_expense: 'recurring',
  bill_due: 'bills',
};

// ============================================
// Context Types
// ============================================

interface CalendarContextState {
  commitmentsLoading: boolean;
  commitmentNotices: string[];
  // Combined calendar items
  calendarItems: UnifiedCalendarItem[];

  // Loading state (derived from AppDataProvider)
  eventsLoading: boolean;
  jobsLoading: boolean;
  invoicesLoading: boolean;
  repeatingInvoicesLoading: boolean;

  /** Per-layer {enabled, loading, connected, error, count, statusKnown} — see CalendarLayerStatus. */
  layers: CalendarLayerStatusMap;

  // Filter state (local to calendar)
  filters: CalendarFilterState;

  // Date range for calendar view (local to calendar)
  viewRangeStart: Date;
  viewRangeEnd: Date;

  // Xero connection status (from AppDataProvider)
  xeroConnected: boolean;
}

interface CalendarContextActions {
  setFilters: (filters: Partial<CalendarFilterState>) => void;
  setViewRange: (start: Date, end: Date) => void;
  refreshAll: () => Promise<void>;
}

type CalendarContextType = CalendarContextState & CalendarContextActions;

// ============================================
// Default State
// ============================================

const getDefaultRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 3, 0);
  return { start, end };
};

const defaultRange = getDefaultRange();

// ============================================
// Context
// ============================================

const CalendarContext = createContext<CalendarContextType | null>(null);

export function useCalendar(): CalendarContextType {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  return context;
}

// ============================================
// Provider
// ============================================

interface CalendarProviderProps {
  children: ReactNode;
  initialFilters?: Partial<CalendarFilterState>;
  customerIds?: string[];
  refreshKey?: string;
}

export function CalendarProvider({ children, initialFilters, customerIds, refreshKey = '' }: CalendarProviderProps) {
  const {
    events,
    jobs,
    invoices,
    bills,
    eventsLoading,
    eventsError,
    jobsLoading,
    jobsError,
    xeroLoading,
    xeroError,
    xeroConnected,
    refreshAll: refreshAppData,
  } = useAppData();
  const { repeatingInvoices, loading: repeatingInvoicesLoading } = useRepeatingInvoice();

  // Local calendar state
  const [filters, setFiltersState] = useState<CalendarFilterState>(() => ({ ...DEFAULT_CALENDAR_FILTERS, ...initialFilters }));
  const [viewRangeStart, setViewRangeStart] = useState<Date>(defaultRange.start);
  const [viewRangeEnd, setViewRangeEnd] = useState<Date>(defaultRange.end);
  const [refreshRevision, setRefreshRevision] = useState(0);
  const sourceRevision = JSON.stringify(jobs.filter((j: any) => j.support).map((j: any) => [j.id, j.lastEdited, j.support.state]))
    + JSON.stringify(invoices.map(i => [i.InvoiceID, i.AmountPaid])) + JSON.stringify([refreshKey, refreshRevision]);
  const commitments = useCalendarCommitments(toLocalDateString(viewRangeStart), toLocalDateString(viewRangeEnd), sourceRevision);
  const commitmentNotices = useMemo(() => {
    if (filters.showSupport === false && filters.showStripePayments === false) return [];
    if (commitments.error) return [commitments.error];
    return (['support', 'stripe'] as const).flatMap(key => {
      if (key === 'support' ? filters.showSupport === false : filters.showStripePayments === false) return [];
      const error = commitments.data?.errors[key];
      const warnings = commitments.data?.warnings[key] || [];
      return [...(error ? [error] : []), ...warnings];
    });
  }, [commitments, filters.showSupport, filters.showStripePayments]);

  // ============================================
  // Combined Calendar Items
  // ============================================

  const calendarItems = useMemo(() => {
    const items: UnifiedCalendarItem[] = [];

    // The view range is a span of whole days, so compare on day boundaries —
    // otherwise an event at 2pm on the last day of the range falls outside it.
    const rangeStartMs = startOfLocalDay(viewRangeStart).getTime();
    const rangeEndMs = endOfLocalDay(viewRangeEnd).getTime();
    // Recurrence is walked in 'YYYY-MM-DD' space, so the range bounds are needed
    // as day strings too. Strings compare lexicographically = calendar order,
    // with no offset for a DST change to get wrong.
    const rangeStartDay = toLocalDateString(viewRangeStart);
    const rangeEndDay = toLocalDateString(viewRangeEnd);

    // Add events
    if (filters.showEvents) {
      for (const event of events) {
        // Soft delete (W2-5): a deleted event is marked, not removed, so its
        // history survives. Legacy documents carry NO status field at all —
        // testing `=== 'active'` here would hide every event ever created.
        if (event.status === 'cancelled') continue;

        // Generate occurrences for recurring events. A null result means the
        // stored recurrence value is not one we understand, in which case the
        // event is treated as a plain one-off (what the old code effectively did).
        const recurrenceType = event.recurrence && event.recurrence.length > 0
          ? event.recurrence[0]
          : null;
        const occurrences = recurrenceType
          ? generateEventOccurrences(event, recurrenceType, rangeStartDay, rangeEndDay)
          : null;

        if (occurrences) {
          for (const occ of occurrences) {
            // Pass the occurrence's own day so each one gets a UNIQUE id plus
            // `baseEventId`. Every occurrence used to carry the base document's
            // id, so editing the 5th occurrence rewrote the series from its
            // original date and deleting it wiped the whole series.
            items.push(eventToCalendarItem(occ.event, occ.day));
          }
          continue;
        }

        // Plain events are range-filtered just like invoices and bills, so all four
        // filter counts describe the same window. (Recurring events are already
        // expanded per-range above and must not be filtered again here.)
        // An unparseable start still gets through — never hide data over a bad date.
        const startMs = parseItemDate(event.start).getTime();
        if (isNaN(startMs) || (startMs >= rangeStartMs && startMs <= rangeEndMs)) {
          items.push(eventToCalendarItem(event));
        }
      }
    }

    // Add jobs on their due date (W2-1). Same source and same open/closed test as
    // the home page's TodayTimeline — open jobs are the owner's #2 daily priority
    // and were the one thing this surface did not show.
    if (filters.showJobs || filters.showSupport !== false) {
      for (const job of jobs) {
        const item = jobToCalendarItem(job);
        if (!item) continue; // closed job, or no usable due date
        const startMs = parseItemDate(item.start).getTime();
        if (isNaN(startMs) || (startMs >= rangeStartMs && startMs <= rangeEndMs)) {
          items.push(item);
        }
      }
    }

    // Add invoices (either due dates or creation dates based on mode)
    // Filter to only include invoices within the visible date range
    if (filters.showInvoices) {
      for (const invoice of invoices) {
        const item = filters.invoiceDateMode === 'created'
          ? invoiceCreationToCalendarItem(invoice)
          : invoiceToCalendarItem(invoice);
        if (item) {
          const itemDate = new Date(item.start);
          if (itemDate >= viewRangeStart && itemDate <= viewRangeEnd) {
            items.push(item);
          }
        }
      }
    }

    // Add recurring templates — filtered by the invoice/bill sub-toggles
    if (filters.showRecurringInvoices) {
      for (const repeating of repeatingInvoices) {
        const isInvoiceTemplate = repeating.templateType !== 'bill';
        const isBillTemplate = repeating.templateType === 'bill';
        if (isInvoiceTemplate && !filters.recurringShowInvoices) continue;
        if (isBillTemplate && !filters.recurringShowBills) continue;
        const occurrences = repeatingInvoiceToCalendarItems(repeating, viewRangeStart, viewRangeEnd);
        items.push(...occurrences);
      }
    }

    // Add actual bills only (Expenses filter — no repeating templates)
    if (filters.showRecurringExpenses) {
      for (const bill of bills) {
        const item = billToCalendarItem(bill);
        if (item) {
          const itemDate = new Date(item.start);
          if (itemDate >= viewRangeStart && itemDate <= viewRangeEnd) {
            items.push(item);
          }
        }
      }
    }

    // Providers hand us items in source order (events, then invoices, then recurring,
    // then bills), which is meaningless to a reader. Sort a copy so every consumer of
    // the context gets the same deterministic, chronological list.
    return mergeCalendarCommitments(items, commitments.data, filters)
      .filter(item => !customerIds || customerIds.includes(calendarCustomerId(item) || ''))
      .sort(compareByStart);
  }, [events, jobs, invoices, bills, repeatingInvoices, filters, viewRangeStart, viewRangeEnd, commitments.data, customerIds]);

  // ============================================
  // Layer status (Task 5)
  // ============================================

  const layers = useMemo<CalendarLayerStatusMap>(() => {
    // Counted off the finished list so a layer's count can never drift from what
    // is actually rendered.
    const counts: Record<CalendarLayerKey, number> = {
      events: 0, jobs: 0, invoices: 0, recurring: 0, bills: 0, support: 0, stripe: 0,
    };
    for (const item of calendarItems) counts[item.commitment?.kind || LAYER_OF_TYPE[item.type]] += 1;

    return {
      support: { key: 'support', label: 'Monthly support', enabled: filters.showSupport !== false,
        loading: commitments.loading, connected: !commitments.error && !commitments.data?.errors.support,
        error: commitments.error || commitments.data?.errors.support || null, count: counts.support, statusKnown: true },
      stripe: { key: 'stripe', label: 'Stripe payments', enabled: filters.showStripePayments !== false,
        loading: commitments.loading, connected: !commitments.error && !commitments.data?.errors.stripe,
        error: commitments.error || commitments.data?.errors.stripe || null, count: counts.stripe, statusKnown: true },
      events: {
        key: 'events',
        label: 'Events',
        enabled: filters.showEvents,
        loading: eventsLoading,
        connected: true, // Firestore listener; a failure surfaces as `error`
        error: eventsError,
        count: counts.events,
        statusKnown: true,
      },
      jobs: {
        key: 'jobs',
        label: 'Jobs',
        enabled: filters.showJobs,
        loading: jobsLoading,
        connected: true,
        error: jobsError,
        count: counts.jobs,
        statusKnown: true,
      },
      invoices: {
        key: 'invoices',
        label: 'Invoices',
        enabled: filters.showInvoices,
        loading: xeroLoading,
        connected: xeroConnected,
        error: xeroError,
        count: counts.invoices,
        // See CalendarLayerStatus.statusKnown: the invoices listener reports
        // neither loading nor errors, so these three flags are not evidence.
        statusKnown: false,
      },
      recurring: {
        key: 'recurring',
        label: 'Recurring',
        // The two sub-toggles (recurringShowInvoices / recurringShowBills) filter
        // WITHIN this layer; the layer itself is on or off with showRecurringInvoices.
        enabled: filters.showRecurringInvoices,
        loading: repeatingInvoicesLoading,
        connected: true,
        error: null, // RepeatingInvoiceContext logs snapshot errors, exposes none
        count: counts.recurring,
        statusKnown: true,
      },
      bills: {
        key: 'bills',
        label: 'Bills',
        // `showRecurringExpenses` is a historical misnomer: it gates ACTUAL bills,
        // not recurring templates (those live in the `recurring` layer).
        enabled: filters.showRecurringExpenses,
        loading: xeroLoading,
        connected: xeroConnected,
        error: xeroError,
        count: counts.bills,
        statusKnown: false,
      },
    };
  }, [
    calendarItems,
    filters,
    eventsLoading,
    eventsError,
    jobsLoading,
    jobsError,
    xeroLoading,
    xeroError,
    xeroConnected,
    repeatingInvoicesLoading, commitments,
  ]);

  // ============================================
  // Actions
  // ============================================

  const setFilters = useCallback((newFilters: Partial<CalendarFilterState>) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }));
  }, []);

  const setViewRange = useCallback((start: Date, end: Date) => {
    setViewRangeStart(start);
    setViewRangeEnd(end);
  }, []);

  const refreshAll = useCallback(async () => {
    setRefreshRevision(value => value + 1);
    await refreshAppData();
  }, [refreshAppData]);

  // ============================================
  // Context Value
  // ============================================

  const value: CalendarContextType = useMemo(() => ({
    commitmentsLoading: commitments.loading,
    commitmentNotices,
    calendarItems,
    eventsLoading,
    jobsLoading,
    invoicesLoading: xeroLoading,
    repeatingInvoicesLoading,
    layers,
    filters,
    viewRangeStart,
    viewRangeEnd,
    xeroConnected,
    setFilters,
    setViewRange,
    refreshAll,
  }), [
    commitments.loading, commitmentNotices,
    calendarItems,
    eventsLoading,
    jobsLoading,
    xeroLoading,
    repeatingInvoicesLoading,
    layers,
    filters,
    viewRangeStart,
    viewRangeEnd,
    xeroConnected,
    setFilters,
    setViewRange,
    refreshAll,
  ]);

  return <CalendarContext.Provider value={value}>{children}</CalendarContext.Provider>;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Parse a calendar start value into a local Date.
 *
 * All-day items store 'YYYY-MM-DD' while timed items store a full ISO string, and
 * `new Date('2026-08-03')` is parsed as UTC midnight — which in Adelaide is mid-morning
 * the same day, enough to push an all-day item outside a range that ends at local
 * midnight. Date-only values are therefore built as local midnight explicitly.
 */
function parseItemDate(value: string): Date {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  return new Date(value);
}

/** Local midnight at the start of the given day */
function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** The last millisecond of the given day, local time */
function endOfLocalDay(date: Date): Date {
  const end = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Chronological ordering for calendar items: by day, then all-day items first
 * (they belong at the top of a day, above the timed ones), then by time.
 */
function compareByStart(a: UnifiedCalendarItem, b: UnifiedCalendarItem): number {
  const aDate = parseItemDate(a.start);
  const bDate = parseItemDate(b.start);

  const aDay = toLocalDateString(aDate);
  const bDay = toLocalDateString(bDate);
  if (aDay !== bDay) return aDay < bDay ? -1 : 1;

  const aAllDay = a.allDay ? 0 : 1;
  const bAllDay = b.allDay ? 0 : 1;
  if (aAllDay !== bAllDay) return aAllDay - bAllDay;

  return aDate.getTime() - bDate.getTime();
}

// ============================================
// Recurring events
// ============================================

/** Recurrence rules that advance by a fixed number of days. */
const FIXED_STEP_DAYS: Record<string, number> = { DAILY: 1, WEEKLY: 7 };

/**
 * How many steps the catch-up loop may take before giving up on a MONTHLY /
 * YEARLY series. 1,200 monthly steps is a century — no honest event reaches it,
 * so hitting the cap means the data is malformed and we stop rather than spin.
 * (DAILY/WEEKLY never loop at all; they are fast-forwarded arithmetically.)
 */
const MAX_CATCHUP_STEPS = 1200;

/**
 * Ceiling on occurrences materialised for one series in one view. The default
 * view range spans about five months, so a DAILY event legitimately needs ~150.
 */
const MAX_OCCURRENCES = 400;

/** Add whole days to a 'YYYY-MM-DD' at UTC noon — far enough from either midnight
 *  that no DST shift or half-hour offset can land on a neighbouring day. */
function addDayString(day: string, days: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Whole days between two 'YYYY-MM-DD' strings (b - a), measured at UTC noon. */
function dayStringDiff(a: string, b: string): number {
  const ms = new Date(`${b}T12:00:00Z`).getTime() - new Date(`${a}T12:00:00Z`).getTime();
  return Math.round(ms / 86400000);
}

/**
 * Advance one occurrence day, or null if the rule is not one we understand.
 *
 * MONTHLY/YEARLY delegate to the shared, backend-faithful `addFrequency`, which
 * clamps to the anchor day-of-month. The old inline `setMonth()` rolled a
 * Jan-31 series to Mar-3 and skipped February entirely.
 */
function stepOccurrenceDay(day: string, recurrence: string, anchorDay: string): string | null {
  const fixed = FIXED_STEP_DAYS[recurrence];
  if (fixed) return addDayString(day, fixed);
  try {
    return addFrequency(day, recurrence, anchorDay);
  } catch {
    return null; // unknown frequency — the caller falls back to a one-off event
  }
}

/** One materialised occurrence: the shifted event copy plus the day it falls on. */
interface EventOccurrence {
  event: CalendarEvent;
  day: string; // 'YYYY-MM-DD'
}

/**
 * Generate occurrences for a recurring event within a date range.
 *
 * Returns null when `recurrence` is not a rule we understand, which tells the
 * caller to render the event as a plain one-off. An empty array means the rule
 * IS understood and simply produces nothing in this window (e.g. the series
 * ended before it).
 *
 * The whole walk happens in 'YYYY-MM-DD' space. Date-object comparisons against
 * the range and end bounds drop the final occurrence when the window straddles
 * an ACDT->AEST change; string comparison is calendar order with no offset to
 * get wrong.
 */
function generateEventOccurrences(
  event: CalendarEvent,
  recurrence: string,
  rangeStartDay: string,
  rangeEndDay: string
): EventOccurrence[] | null {
  const startDate = new Date(event.start);
  if (isNaN(startDate.getTime())) return null; // unusable start; treat as one-off
  const anchorDay = toLocalDateString(startDate);

  if (!stepOccurrenceDay(anchorDay, recurrence, anchorDay)) return null;

  // recurrenceEnd is INCLUSIVE, and absent on every legacy document — an absent
  // value means "never ends", NOT "already ended".
  const endDay = event.recurrenceEnd || null;
  if (endDay && endDay < rangeStartDay) return [];

  const endDate = new Date(event.end);
  const hasEnd = !isNaN(endDate.getTime());

  // Shift by WHOLE DAYS with local setDate: that preserves the wall-clock time
  // across a DST change (adding a fixed number of ms would not) and keeps the
  // duration in days. Matches what eventToCalendarItem does, so the item's
  // start/end and its eventData copy can never disagree.
  const makeOccurrence = (day: string): EventOccurrence => {
    const shiftDays = dayStringDiff(anchorDay, day);
    const s = new Date(startDate);
    s.setDate(s.getDate() + shiftDays);
    let end = event.end;
    if (hasEnd) {
      const e = new Date(endDate);
      e.setDate(e.getDate() + shiftDays);
      end = event.allDay ? toLocalDateString(e) : e.toISOString();
    }
    return {
      event: {
        ...event,
        start: event.allDay ? toLocalDateString(s) : s.toISOString(),
        end,
      },
      day,
    };
  };

  // Fast-forward to the first occurrence at or after the range start.
  let current = anchorDay;
  if (current < rangeStartDay) {
    const fixed = FIXED_STEP_DAYS[recurrence];
    if (fixed) {
      // Arithmetic, not iteration: the old loop stepped one period at a time, so
      // a DAILY event created in 2019 burned ~2,400 iterations on EVERY render.
      const periods = Math.ceil(dayStringDiff(current, rangeStartDay) / fixed);
      current = addDayString(current, periods * fixed);
    } else {
      let steps = 0;
      while (current < rangeStartDay) {
        if (steps >= MAX_CATCHUP_STEPS) {
          // Bail safely: showing nothing for this series beats freezing the tab.
          console.warn(
            `[Calendar] recurring event ${event.id} exceeded ${MAX_CATCHUP_STEPS} catch-up steps; skipping it`
          );
          return [];
        }
        const next = stepOccurrenceDay(current, recurrence, anchorDay);
        if (!next || next <= current) return []; // non-advancing cursor
        current = next;
        steps++;
      }
    }
  }

  // Days the user deleted individually. Written by NewEventModal's
  // "this occurrence only" delete; without this the exception is stored and the
  // occurrence keeps rendering, which looks exactly like the delete failed.
  const exceptions = Array.isArray(event.exceptions) ? new Set(event.exceptions) : null;

  const occurrences: EventOccurrence[] = [];
  while (current <= rangeEndDay && occurrences.length < MAX_OCCURRENCES) {
    if (endDay && current > endDay) break; // series finished mid-window
    if (exceptions && exceptions.has(current)) {
      // Skipped, but still step the cursor below so the series continues.
      const skipNext = stepOccurrenceDay(current, recurrence, anchorDay);
      if (!skipNext || skipNext <= current) break;
      current = skipNext;
      continue;
    }
    occurrences.push(makeOccurrence(current));
    const next = stepOccurrenceDay(current, recurrence, anchorDay);
    if (!next || next <= current) break;
    current = next;
  }

  return occurrences;
}

export default CalendarContext;
